import React, { useEffect, useMemo, useState } from "react";
import { ActivityIndicator, StyleSheet, TouchableOpacity, View } from "react-native";

import { colors, radii, spacing } from "../../../../styles/design-system";
import { Plus, Sparkle, Trash, X } from "@phosphor-icons/react";
import { Controller, useFieldArray, useForm, useWatch } from "react-hook-form";

import CustomDropdown, { type CustomDropdownOption } from "../../../common/general/CustomDropDown/CustomDropdown";
import CustomButton from "../../../common/general/CustomButton/CustomButton";
import CustomModal from "../../../common/general/CustomModal/CustomModal";
import CustomText from "../../../common/general/CustomText/CustomText";
import CustomTextInput from "../../../common/general/CustomTextInput/CustomTextInput";
import { useToast } from "../../../../contexts/Toast/ToastProvider";
import {
  useGenerateSocraticRubricForQuestionMutation,
  useCreateSocraticQuestionMutation,
  useUpdateSocraticQuestionMutation,
  type SocraticQuestionRubricInput,
} from "../../../../graphql/generated/graphql";
import type { AiTutor, Question } from "../../../../graphql/generated/graphql";

type SocraticQuestionEditorMode = "create" | "edit";
type SocraticQuestionRubricRow = {
  component: string;
  points: string;
};
type SocraticQuestionEditorForm = {
  question: string;
  totalScore: string;
  selectedTutorId: string;
  rubrics: SocraticQuestionRubricRow[];
};

type SocraticQuestionEditorModalProps = {
  visible: boolean;
  mode: SocraticQuestionEditorMode;
  aiTutors: AiTutor[];
  assessmentId?: string;
  selectedAiTutorId?: string | null;
  question?: Question | null;
  draftMode?: boolean;
  onDraftSave?: (question: {
    id?: string | null;
    aiTutorId: string;
    question: string;
    totalScore?: number | null;
    rubrics: SocraticQuestionRubricInput[];
  }) => Promise<void> | void;
  onClose: () => void;
  onSaved: () => Promise<void> | void;
};

const normalizeRubricRows = (rows: Array<{ component: string; points: string }>) => {
  const normalizedRubrics: SocraticQuestionRubricInput[] = [];

  for (const row of rows) {
    const component = row.component.trim();
    const pointsInput = row.points.trim();
    const hasComponent = component.length > 0;
    const hasPointsInput = pointsInput.length > 0;
    const parsedPoints = Number(pointsInput);

    if (!hasComponent && !hasPointsInput) {
      continue;
    }

    if (!hasComponent || !hasPointsInput || !Number.isFinite(parsedPoints) || parsedPoints <= 0) {
      return { rubrics: [], error: "Each rubric row must include a label and a positive point value." };
    }

    normalizedRubrics.push({ component, points: parsedPoints });
  }

  return { rubrics: normalizedRubrics, error: null };
};

const normalizeTotalScore = (totalScoreInput: string) => {
  if (!totalScoreInput.trim()) {
    return { value: undefined as number | undefined, error: null };
  }

  const parsedTotalScore = Number(totalScoreInput.trim());
  if (!Number.isFinite(parsedTotalScore) || parsedTotalScore <= 0) {
    return { value: undefined as number | undefined, error: "Points must be a positive number." };
  }

  return { value: parsedTotalScore, error: null };
};

const MIN_QUESTION_LENGTH = 10;

const SocraticQuestionEditorModal = ({
  visible,
  mode,
  aiTutors,
  assessmentId,
  selectedAiTutorId,
  question,
  draftMode = false,
  onDraftSave,
  onClose,
  onSaved,
}: SocraticQuestionEditorModalProps) => {
  const { showToast } = useToast();
  const [createSocraticQuestion, { loading: isCreating }] = useCreateSocraticQuestionMutation();
  const [updateSocraticQuestion, { loading: isUpdating }] = useUpdateSocraticQuestionMutation();
  const [generateSocraticRubrics, { loading: isGeneratingRubrics }] = useGenerateSocraticRubricForQuestionMutation();
  const { control, reset, handleSubmit, setValue, getValues } = useForm<SocraticQuestionEditorForm>({
    defaultValues: {
      question: "",
      totalScore: "",
      selectedTutorId: "",
      rubrics: [],
    },
  });
  const {
    fields: rubricFields,
    append: appendRubric,
    remove: removeRubric,
    replace: replaceRubrics,
  } = useFieldArray({
    control,
    name: "rubrics",
  });

  const questionText = useWatch({ control, name: "question" });
  const selectedTutorId = useWatch({ control, name: "selectedTutorId" });
  const watchedRubrics = useWatch({ control, name: "rubrics" }) ?? [];
  const isSubmitting = isCreating || isUpdating;
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const isEditMode = mode === "edit";
  const modalTitle = isEditMode ? "Edit Question" : "Add Question";
  const submitText = isEditMode ? "Save Changes" : "Add Question";

  const tutorOptions: CustomDropdownOption[] = useMemo(
    () =>
      aiTutors.map((tutor) => ({
        label: tutor.title ?? "Interactive Learning Experience",
        value: tutor.id,
      })),
    [aiTutors]
  );

  const normalizedQuestionLength = questionText?.trim().length ?? 0;
  const hasRubrics = rubricFields.length > 0;
  const shouldShowRubrics = isEditMode || hasRubrics;
  const canGenerateRubrics =
    Boolean(questionText?.trim()) && normalizedQuestionLength >= MIN_QUESTION_LENGTH && Boolean(selectedTutorId);
  const { rubrics: normalizedWatchedRubrics, error: watchedRubricError } = useMemo(
    () => normalizeRubricRows(watchedRubrics),
    [watchedRubrics]
  );
  const hasAtLeastOneRubric = normalizedWatchedRubrics.length > 0;

  const rubricTotalPts = useMemo(() => {
    return watchedRubrics.reduce((sum, row) => {
      const pts = Number(row?.points ?? "");
      return sum + (Number.isFinite(pts) && pts > 0 ? pts : 0);
    }, 0);
  }, [watchedRubrics]);

  useEffect(() => {
    if (!visible) {
      return;
    }

    if (isEditMode && question) {
      const resetRubrics = (question.rubrics ?? []).map((rubric) => ({
        component: rubric.component ?? "",
        points: typeof rubric.points === "number" ? String(rubric.points) : "",
      }));

      reset({
        question: question.question ?? "",
        totalScore: typeof question.totalScore === "number" ? String(question.totalScore) : "",
        selectedTutorId: selectedAiTutorId ?? question.aiTutor?.id ?? "",
        rubrics: resetRubrics,
      });
      replaceRubrics(resetRubrics);
    } else {
      reset({
        question: "",
        totalScore: "",
        selectedTutorId: selectedAiTutorId ?? aiTutors[0]?.id ?? "",
        rubrics: [],
      });
      replaceRubrics([]);
    }

    setErrorMessage(null);
  }, [visible, isEditMode, question, selectedAiTutorId, aiTutors, reset, replaceRubrics]);

  const canSubmit = useMemo(() => {
    const hasQuestion = Boolean(questionText?.trim());
    const hasSelectedTutor = Boolean(selectedTutorId);
    const isQuestionLongEnough = isEditMode ? hasQuestion : normalizedQuestionLength >= MIN_QUESTION_LENGTH;
    const hasRequiredQuestionFields = isEditMode ? hasQuestion : isQuestionLongEnough && hasSelectedTutor;

    return hasRequiredQuestionFields && !watchedRubricError && hasAtLeastOneRubric;
  }, [questionText, normalizedQuestionLength, selectedTutorId, isEditMode, watchedRubricError, hasAtLeastOneRubric]);

  const handleAddRubric = () => {
    appendRubric({ component: "", points: "" });
    setErrorMessage(null);
  };

  const handleRemoveRubric = (index: number) => {
    removeRubric(index);
    setErrorMessage(null);
  };

  const handleGenerateRubrics = async () => {
    setErrorMessage(null);

    const normalizedQuestion = String(getValues("question") ?? "").trim();
    if (normalizedQuestion.length < MIN_QUESTION_LENGTH) {
      setErrorMessage(`Question must be at least ${MIN_QUESTION_LENGTH} characters.`);
      return;
    }

    const normalizedTotalScoreInput = String(getValues("totalScore") ?? "");
    const { value: normalizedTotalScore, error: totalScoreError } = normalizeTotalScore(normalizedTotalScoreInput);
    if (totalScoreError) {
      setErrorMessage(totalScoreError);
      return;
    }

    try {
      const { data: mutationData } = await generateSocraticRubrics({
        variables: {
          input: {
            aiTutorId: String(selectedTutorId),
            question: normalizedQuestion,
            ...(normalizedTotalScore !== undefined && { totalScore: normalizedTotalScore }),
          },
        },
      });

      const response = mutationData?.generateSocraticRubricForQuestion;
      if (!response?.success) {
        setErrorMessage(response?.message || "Failed to generate rubrics.");
        return;
      }

      const generatedRubrics = (response.rubrics ?? [])
        .map((rubric) => ({
          component: rubric.component,
          points: String(rubric.points),
        }))
        .filter((rubric) => rubric.component.trim().length > 0 && rubric.points.trim().length > 0);

      if (generatedRubrics.length === 0) {
        setErrorMessage("No rubrics were generated. Please try again.");
        replaceRubrics([]);
        return;
      }

      replaceRubrics(generatedRubrics);

      if (response.totalScore !== null && response.totalScore !== undefined) {
        setValue("totalScore", String(response.totalScore));
      }
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : "Failed to generate rubrics.");
    }
  };

  const onSubmit = async (data: SocraticQuestionEditorForm) => {
    setErrorMessage(null);

    const normalizedQuestion = String(data.question ?? "").trim();
    const selectedTutorId = String(data.selectedTutorId ?? "");
    const rubrics = data.rubrics ?? [];
    const normalizedTotalScoreInput = String(data.totalScore ?? "");

    const { rubrics: normalizedRubrics, error: rubricError } = normalizeRubricRows(rubrics);
    const { value: normalizedTotalScore, error: totalScoreError } = normalizeTotalScore(normalizedTotalScoreInput);

    if (!normalizedQuestion) {
      setErrorMessage("Question is required.");
      return;
    }

    if (!isEditMode && normalizedQuestion.length < MIN_QUESTION_LENGTH) {
      setErrorMessage(`Question must be at least ${MIN_QUESTION_LENGTH} characters.`);
      return;
    }

    if (!isEditMode && !selectedTutorId) {
      setErrorMessage("Please select an ILE.");
      return;
    }

    if (rubricError) {
      setErrorMessage(rubricError);
      return;
    }
    if (normalizedRubrics.length === 0) {
      setErrorMessage("At least one rubric is required.");
      return;
    }

    if (totalScoreError) {
      setErrorMessage(totalScoreError);
      return;
    }

    try {
      if (draftMode) {
        await onDraftSave?.({
          id: question?.id ?? null,
          aiTutorId: isEditMode ? selectedAiTutorId || question?.aiTutor?.id || selectedTutorId : selectedTutorId,
          question: normalizedQuestion,
          totalScore: normalizedTotalScore ?? null,
          rubrics: normalizedRubrics,
        });
        await onSaved();
        showToast({
          message: isEditMode ? "Question updated successfully." : "Question added successfully.",
          type: "success",
        });
        onClose();
        return;
      }

      if (isEditMode) {
        if (!question?.id) {
          setErrorMessage("Question ID is missing for edit.");
          return;
        }

        const { data: mutationData } = await updateSocraticQuestion({
          variables: {
            questionId: question.id,
            assessmentId,
            input: {
              question: normalizedQuestion,
              rubrics: normalizedRubrics,
              ...(normalizedTotalScore !== undefined && { totalScore: normalizedTotalScore }),
            },
          } as any,
        });

        const response = mutationData?.updateSocraticQuestion;
        if (!response?.success) {
          setErrorMessage(response?.message || "Failed to update question.");
          return;
        }
      } else {
        const createPayload = {
          aiTutorId: selectedTutorId,
          ...(assessmentId ? { assessmentId } : {}),
          question: normalizedQuestion,
          rubrics: normalizedRubrics,
          ...(normalizedTotalScore !== undefined && { totalScore: normalizedTotalScore }),
        };

        const { data: mutationData } = await createSocraticQuestion({
          variables: {
            input: createPayload as any,
          },
        });

        const response = mutationData?.createSocraticQuestion;
        if (!response?.success) {
          setErrorMessage(response?.message || "Failed to add question.");
          return;
        }
      }

      await onSaved();

      showToast({
        message: isEditMode ? "Question updated successfully." : "Question added successfully.",
        type: "success",
      });
      onClose();
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : "Something went wrong. Please try again.");
    }
  };

  return (
    <CustomModal
      visible={visible}
      onRequestClose={onClose}
      ariaLabel={modalTitle}
      style={styles.modal}
      bodyStyle={styles.modalBody}>
      <View style={styles.container}>
        <View style={styles.headerRow}>
          <CustomText text={modalTitle} useTranslationText={false} size="xl" weight="bold" style={styles.modalTitle} />
          <TouchableOpacity
            style={styles.closeButton}
            onPress={onClose}
            accessibilityRole="button"
            aria-label="Close question editor modal">
            <X size={18} color={colors.grey[500]} />
          </TouchableOpacity>
        </View>

        <View style={styles.content}>
          <View style={styles.fieldGroup}>
            <CustomText text="Question" useTranslationText={false} weight="semiBold" style={styles.fieldLabel} />
            <View style={styles.inputWrap}>
              <Controller
                control={control}
                name="question"
                render={({ field: { value, onChange, onBlur } }) => (
                  <CustomTextInput
                    value={value}
                    onChangeText={(inputValue) => {
                      onChange(inputValue);
                      setErrorMessage(null);
                    }}
                    onBlur={() => {
                      onBlur();
                      if (!isEditMode && !hasRubrics && canGenerateRubrics) {
                        handleGenerateRubrics();
                      }
                    }}
                    placeholder="Enter your question..."
                    multiline={true}
                    numberOfLines={4}
                    style={styles.questionInput}
                  />
                )}
              />
            </View>
          </View>

          {!isEditMode && (
            <View style={styles.fieldGroup}>
              <CustomText
                text="Interactive Learning Experience"
                useTranslationText={false}
                weight="semiBold"
                style={styles.fieldLabel}
              />
              <Controller
                control={control}
                name="selectedTutorId"
                render={({ field: { value, onChange } }) => (
                  <CustomDropdown
                    options={tutorOptions}
                    value={value}
                    onChange={onChange}
                    placeholder="Select an ILE"
                    fullWidth={true}
                    containerStyle={styles.dropdown}
                    disabled={aiTutors.length === 0}
                  />
                )}
              />
            </View>
          )}

          <View style={styles.fieldGroup}>
            <View style={styles.rubricHeaderRow}>
              <CustomText
                text="Rubric Criteria"
                useTranslationText={false}
                weight="semiBold"
                style={styles.fieldLabel}
              />
              <View style={styles.rubricHeaderActions}>
                <TouchableOpacity
                  onPress={handleGenerateRubrics}
                  disabled={!canGenerateRubrics || isGeneratingRubrics}
                  style={[
                    styles.generateRubricLink,
                    (!canGenerateRubrics || isGeneratingRubrics) && styles.generateRubricLinkDisabled,
                  ]}
                  accessibilityRole="button"
                  aria-label="Generate rubric criteria">
                  <View style={styles.generateRubricIcon}>
                    <Sparkle
                      size={14}
                      color={!canGenerateRubrics || isGeneratingRubrics ? colors.grey[400] : colors.brand[500]}
                      weight="fill"
                    />
                  </View>
                  <CustomText
                    text={isGeneratingRubrics ? "Generating..." : hasRubrics ? "Regenerate Rubric" : "Generate Rubric"}
                    useTranslationText={false}
                    style={[
                      styles.generateRubricLinkText,
                      (!canGenerateRubrics || isGeneratingRubrics) && styles.generateRubricLinkTextDisabled,
                    ]}
                  />
                </TouchableOpacity>
                <CustomText
                  text={`Total: ${rubricTotalPts} pts`}
                  useTranslationText={false}
                  style={styles.rubricTotalText}
                />
              </View>
            </View>

            {!hasRubrics && !isEditMode ? (
              <View style={styles.rubricEmptyState}>
                <CustomText
                  text='Click "Generate Rubric" or tab out of the question field to auto-generate.'
                  useTranslationText={false}
                  style={styles.rubricEmptyInstruction}
                />
              </View>
            ) : (
              <>
                {rubricFields.map((rubric, rubricIndex) => (
                  <View key={rubric.id} style={styles.rubricRow}>
                    <View style={styles.rubricInputWrap}>
                      <Controller
                        control={control}
                        name={`rubrics.${rubricIndex}.component`}
                        render={({ field: { value, onBlur, onChange } }) => (
                          <CustomTextInput
                            value={value ?? ""}
                            onBlur={onBlur}
                            onChangeText={(inputValue) => {
                              onChange(inputValue);
                              setErrorMessage(null);
                            }}
                            placeholder="Criterion description"
                          />
                        )}
                      />
                    </View>
                    <View style={styles.rubricPointsRow}>
                      <Controller
                        control={control}
                        name={`rubrics.${rubricIndex}.points`}
                        render={({ field: { value, onBlur, onChange } }) => (
                          <CustomTextInput
                            value={value ?? ""}
                            onBlur={onBlur}
                            onChangeText={(inputValue) => {
                              onChange(inputValue);
                              setErrorMessage(null);
                            }}
                            placeholder=""
                            keyboardType="numeric"
                            style={styles.rubricPointsInput}
                          />
                        )}
                      />
                      <CustomText text="pts" useTranslationText={false} style={styles.rubricPointsLabel} />
                    </View>
                    <TouchableOpacity
                      style={styles.deleteRubricButton}
                      onPress={() => handleRemoveRubric(rubricIndex)}
                      accessibilityRole="button"
                      aria-label={`Remove rubric ${rubricIndex + 1}`}>
                      <Trash size={16} color={colors.grey[500]} />
                    </TouchableOpacity>
                  </View>
                ))}

                {shouldShowRubrics && (
                  <TouchableOpacity
                    onPress={handleAddRubric}
                    style={styles.addCriterionLink}
                    accessibilityRole="button"
                    aria-label="Add criterion">
                    <Plus size={16} color={colors.brand[500]} />
                    <CustomText text="Add Criterion" useTranslationText={false} style={styles.addCriterionLinkText} />
                  </TouchableOpacity>
                )}
              </>
            )}
          </View>
        </View>

        {!!errorMessage && <CustomText text={errorMessage} useTranslationText={false} style={styles.errorText} />}

        <View style={styles.footer}>
          <CustomButton
            text="Cancel"
            useTranslationText={false}
            styleType="primaryTransparentStroke"
            onPress={onClose}
            disabled={isSubmitting}
            textStyle={styles.cancelButtonText}
            style={styles.cancelButton}
          />
          {isSubmitting ? (
            <View style={styles.submitButtonLoading}>
              <ActivityIndicator size="small" color={colors.white} />
            </View>
          ) : (
            <CustomButton
              text={submitText}
              useTranslationText={false}
              size="s"
              onPress={handleSubmit(onSubmit)}
              styleType="primary"
              disabled={!canSubmit}
              textStyle={styles.submitButtonText}
              style={styles.submitButton}
            />
          )}
        </View>
      </View>
    </CustomModal>
  );
};

const styles = StyleSheet.create({
  modal: {
    maxWidth: 740,
    paddingHorizontal: 0,
    paddingVertical: 0,
    borderRadius: 18,
    overflow: "hidden",
  },
  modalBody: {
    marginTop: 0,
    flexGrow: 0,
  },
  container: {
    width: "100%",
  },
  headerRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: spacing.xl,
    paddingTop: spacing.lg,
    paddingBottom: spacing.md,
    backgroundColor: colors.white,
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
  },
  modalTitle: {
    color: colors.grey[900],
  },
  closeButton: {
    width: 28,
    height: 28,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: radii.md,
  },
  content: {
    paddingHorizontal: spacing.xl,
    paddingTop: spacing.md,
    paddingBottom: spacing.md,
    gap: spacing.md,
  },
  fieldGroup: {
    gap: spacing.xs,
  },
  fieldLabel: {
    color: colors.grey[700],
  },
  inputWrap: {
    width: "100%",
  },
  questionInput: {
    minHeight: 96,
    textAlignVertical: "top",
    borderRadius: radii.lg,
  },
  dropdown: {
    width: "100%",
  },
  ielevelText: {
    color: colors.grey[700],
    paddingHorizontal: spacing.xxs,
    paddingVertical: 9,
  },
  rubricHeaderRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: spacing.xs,
  },
  rubricHeaderActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.sm,
  },
  generateRubricLink: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 4,
    paddingHorizontal: 0,
  },
  generateRubricLinkDisabled: {
    opacity: 0.6,
  },
  generateRubricLinkTextDisabled: {
    color: colors.grey[400],
  },
  generateRubricIcon: {
    marginRight: 6,
    justifyContent: "center",
    alignItems: "center",
  },
  generateRubricLinkText: {
    color: colors.brand[500],
    fontSize: 14,
    fontWeight: "600",
  },
  rubricTotalText: {
    color: colors.grey[700],
    fontSize: 14,
  },
  rubricEmptyState: {
    borderWidth: 1,
    borderStyle: "dashed",
    borderColor: colors.grey[300],
    borderRadius: radii.lg,
    minHeight: 100,
    padding: spacing.md,
    justifyContent: "center",
    alignItems: "center",
  },
  rubricEmptyInstruction: {
    color: colors.grey[500],
    fontSize: 14,
    textAlign: "center",
  },
  rubricRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xs,
  },
  rubricInputWrap: {
    flex: 1,
  },
  rubricPointsRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  rubricPointsInput: {
    width: 56,
    minWidth: 56,
  },
  rubricPointsLabel: {
    color: colors.grey[700],
    fontSize: 14,
  },
  deleteRubricButton: {
    width: 30,
    height: 30,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: radii.md,
  },
  addCriterionLink: {
    flexDirection: "row",
    alignItems: "center",
    alignSelf: "flex-start",
    gap: 8,
    paddingVertical: 4,
    paddingHorizontal: 0,
    marginTop: 2,
  },
  addCriterionLinkText: {
    color: colors.brand[500],
    fontSize: 14,
    fontWeight: "600",
  },
  errorText: {
    color: colors.error[500],
    paddingHorizontal: spacing.xl,
    paddingBottom: spacing.xs,
  },
  footer: {
    paddingHorizontal: spacing.xl,
    paddingTop: spacing.sm,
    paddingBottom: 22,
    borderTopWidth: 1,
    borderTopColor: colors.grey[200],
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  cancelButton: {
    minWidth: 84,
    justifyContent: "center",
  },
  cancelButtonText: {
    color: colors.grey[600],
    fontWeight: "600",
  },
  submitButton: {
    minWidth: 138,
    justifyContent: "center",
  },
  submitButtonText: {
    fontWeight: "700",
  },
  submitButtonLoading: {
    minWidth: 138,
    height: 36,
    borderRadius: radii.md,
    backgroundColor: colors.brand[500],
    alignItems: "center",
    justifyContent: "center",
  },
});

export default SocraticQuestionEditorModal;
